import type {
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';

/**
 * Custom email validator that enforces TLD requirements.
 *
 * This validator ensures emails follow the pattern: local@domain.tld
 * where a top-level domain is required. This prevents downstream
 * email delivery failures and ensures consistency across the platform.
 *
 * Examples:
 * - ✅ Valid: user@example.com, test@domain.org
 * - ❌ Invalid: user@example, test@localhost
 *
 * @returns ValidatorFn that returns null for valid emails or { email: true } for invalid ones
 */
export function emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        // Return null for empty/null values - let required validator handle this
        if (!control.value) {
            return null;
        }

        // Ensure we're working with a string value
        const emailValue = String(control.value).trim();

        function isEmail(search: string): boolean {
            const emailRegexp = new RegExp(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            );
            const isEmailValid = emailRegexp.test(search);
            return isEmailValid;
        }
        const isValid = isEmail(emailValue);

        // Return same error structure as Validators.email for compatibility
        return isValid ? null : { email: true };
    };
}
